import { Globe } from 'lucide-react';
import { useState } from 'react';

import { useAuth } from '../../auth/AuthContext';
import { clientOnboardingApi } from '../../lib/api/endpoints';
import { LANGUAGE_OPTIONS } from '../../lib/languageOptions';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

/** Topbar language switcher — updates `client.languagePref` via the same
 * idempotent `POST /client/onboarding` endpoint `EditProfilePage` uses
 * (resubmits the client's existing goals/background unchanged, only
 * `languagePref` differs), then refreshes the auth context so every
 * languagePref-driven copy hook (`useQuestionnaireCopy`, `useBookingCopy`,
 * `useJourneyCopy`) picks it up immediately. Purely a UI-chrome switch —
 * it does not touch any already-generated report's own stored language
 * (see the mismatch banner on the Report page for that). */
export function LanguageToggle(): JSX.Element {
  const { client, refreshMe } = useAuth();
  const [pending, setPending] = useState(false);
  const current = client?.languagePref ?? 'EN';

  const select = async (value: (typeof LANGUAGE_OPTIONS)[number]['value']): Promise<void> => {
    if (!client || value === current || pending) return;
    setPending(true);
    try {
      await clientOnboardingApi.submit({
        goals: client.goals ?? '',
        background: client.background ?? '',
        languagePref: value,
        consentAccepted: true,
      });
      await refreshMe();
    } finally {
      setPending(false);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          disabled={pending}
          aria-label="Change language"
          className="relative flex items-center justify-center w-9 h-9 rounded-full border border-border bg-card text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors disabled:opacity-60"
        >
          <Globe className="w-4 h-4" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {LANGUAGE_OPTIONS.map((opt) => (
          <DropdownMenuItem
            key={opt.value}
            onClick={() => void select(opt.value)}
            className={opt.value === current ? 'font-medium' : undefined}
          >
            {opt.native}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
